import {
    Box,
    Container,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import { debounce, isEmpty } from "lodash";
import React, { useCallback } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
    ListDetailViewSectionContent,
    ListDetailViewSectionHeader,
    ListDetailViewSectionLayout,
    ListViewItem,
} from "../../components/ListDetailView";
import {
    ListView,
    MainPage,
    MainView,
    PageContext,
    usePageState,
} from "../../components/PageLayout";
import {
    FinancesSettingsForm,
    TaxSettingsCard,
    TransactionTypesSettingsCard,
} from "../../modules/financials";
import { TimesheetSettingsCard } from "../../modules/timesheets/TimesheetSettingsCard";
import { useUserInfo } from "../../utils/security";
import SwitchRow from "../../components/SwitchRow";
import dayjs from "dayjs";
import { useForm } from "../../utils/form";

const components = [
    {
        name: "transactionsSupport",
        title: "Finance",
        description: "Spravujte finanční toky - příjmy a výdaje",
    },
    {
        name: "employeesSupport",
        title: "Pracovníci",
        description: "Lorem ipsum",
    },
    {
        name: "timesheetsSupport",
        title: "Výkazy",
        description: "Umožňuje vašim zaměstnancům vykazovat práci",
        //requires: ["employeesSupport"],
    },
    {
        name: "counterpartiesSupport",
        title: "Protistrany",
        description: "Organizujte finance, výkazy a projekty podle zákazníků / dodavatelů",
    },
    {
        name: "projectsSupport",
        title: "Projekty",
        description: "Organizujte výkazy a finance pomocí projektů",
    },
    {
        name: "estimatesSupport",
        title: "Kalkulace",
        description: "Vytvářejte kalkulace pro zákazníky",
    },
    /*
    {
        name: "transactionsTaxSupport",
        title: "Evidovat daň",
        description: "Evidujte daně (DPH, spotřební daň, atd.) na účetních transakcích",
        requires: ["transactionsSupport"],
    },
    {
        name: "billingsSupport",
        title: "Vyúčtování",
        description: "Počítejte zisk / ztrátu na projektech",
    },
    {
        name: "documentsSupport",
        title: "Dokumenty",
        description: "Sdílejte dokumenty mezi zaměstnanci",
    },*/
];

export const hasFunction = (obj, dependencies) => {
    if (Array.isArray(dependencies) && dependencies.length) {
        for (let dependency of dependencies) {
            if (!obj[dependency]) {
                return false;
            }
        }
    }
    return true;
};

export const FunctionSettingsCard = () => {
    const { userInfo, updateUserInfo, userInfoLoading: disabled } = useUserInfo();

    return (
        <>
            <Divider sx={(theme) => ({ borderColor: theme.palette.outlineVariant.main })} />
            {components.map((c, idx) => (
                <div key={idx}>
                    <SwitchRow
                        {...c}
                        value={!!userInfo[c.name]}
                        onChange={(e) => {
                            updateUserInfo({ ...userInfo, [c.name]: e.target.checked });
                        }}
                        disabled={!!disabled || !hasFunction(userInfo, c.requires)}
                        align="right"
                    />
                    <Divider sx={(theme) => ({ borderColor: theme.palette.outlineVariant.main })} />
                </div>
            ))}
        </>
    );
};

export const LocalizationSettingsCard = () => {
    const { userInfo, updateUserInfo, userInfoLoading: disabled } = useUserInfo();
    const numFormat = `${userInfo.decimalDelimiter}${userInfo.thousandDelimiter}`;
    return (
        <>
            <Box mb={6}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Formát čísel
                </Typography>
                <Typography variant="body2">
                    Zvolte, jak má být zobrazeno číslo {(123456.78).toLocaleString()}. Takto se pak
                    budou formátovat všechna čísla v aplikaci
                </Typography>
                <Box my={2}>
                    <ToggleButtonGroup
                        exclusive
                        value={numFormat}
                        onChange={(e) =>
                            updateUserInfo({
                                ...userInfo,
                                decimalDelimiter: e.target.value.charAt(0),
                                thousandDelimiter: e.target.value.charAt(1),
                            })
                        }
                        disabled={disabled}>
                        <ToggleButton value=", ">123 456,78</ToggleButton>
                        <ToggleButton value=".,">123,456.78</ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            </Box>
            <Box mb={6}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Formát datumu
                </Typography>
                <Typography variant="body2">
                    Zvolte, jak má být zobrazeno datum {new Date("2023-12-31").toLocaleDateString()}
                    . Takto se pak budou zobrazovat všechna data v aplikaci
                </Typography>
                <Box my={2}>
                    <ToggleButtonGroup
                        exclusive
                        value={userInfo.dateFormat}
                        onChange={(e) =>
                            updateUserInfo({ ...userInfo, dateFormat: e.target.value })
                        }
                        disabled={disabled}>
                        {["DD.MM.YYYY", "MM/DD/YYYY", "DD/MM/YYYY"].map((format) => (
                            <ToggleButton key={format} value={format}>
                                {dayjs("2023-12-31").format(format)}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            </Box>
        </>
    );
};

export const useSettingsForm = ({ settingsKey, debounceDelay, validate, ...formProps }) => {
    const { userInfo, updateUserInfo, userInfoLoading: progress } = useUserInfo();

    let { values, setValues, errors, setErrors, ...rest } = useForm({
        ...formProps,
        data: !!settingsKey ? userInfo[settingsKey] : userInfo,
    });

    /*
    const validateAndSubmit = useCallback(async () => {
        setErrors({});
    }, [updateUserInfo, setErrors]);
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedUpdateUserInfo = useCallback(
        debounce(updateUserInfo, debounceDelay || 1000, { trailing: true }),
        [updateUserInfo, debounceDelay, debounce]
    );

    const changeValues = useCallback(
        (values) => {
            setValues(values);
            let vals = undefined;
            if (!!settingsKey) {
                vals = { ...userInfo, [settingsKey]: values };
            } else {
                vals = { ...userInfo, ...vals };
            }
            debouncedUpdateUserInfo(vals);
        },
        [setValues, debouncedUpdateUserInfo, userInfo, settingsKey]
    );
    const onChange = useCallback(
        (e) => {
            let isCheckbox = e.target?.type === "checkbox";
            let vals = {
                ...values,
                [e.target?.name]: isCheckbox ? e.target?.checked : e.target?.value,
            };
            changeValues(vals);
        },
        [values, changeValues]
    );

    return {
        ...rest,
        errors,
        setErrors,
        progress,
        values,
        setValues: changeValues,
        handleChange: onChange,
    };
};

const items = [
    { id: "local", name: "Obecné", icon: null, render: () => <LocalizationSettingsCard /> },
    { id: "modules", name: "Výběr funkčností", icon: null, render: () => <FunctionSettingsCard /> },
    {
        id: "subject",
        name: "Informace o společnosti",
        icon: null,
        requires: ["transactionsSupport"],
        render: () => <FinancesSettingsForm />,
    },
    //    { id: "currency", name: "Měny", icon: null, requires: ["transactionsSupport"], render: () => <CurrencySettingsCard /> },
    {
        id: "tax",
        name: "Sazby daně",
        icon: null,
        requires: ["transactionsSupport", "transactionsTaxSupport"],
        render: () => <TaxSettingsCard />,
    },
    {
        id: "transaction-types",
        name: "Typy finančních transakcí",
        icon: null,
        requires: ["transactionsSupport"],
        render: () => <TransactionTypesSettingsCard />,
    },
    {
        id: "timesheets",
        name: "Vykazování",
        icon: null,
        requires: ["timesheetsSupport"],
        render: () => <TimesheetSettingsCard />,
    },
    {
        id: "estimates",
        name: "Kalkulace",
        icon: null,
        requires: ["estimatesSupport"],
        render: () => <></>,
    },
];

const SettingsList = ({ detailId, compact, selectItem }) => {
    const { userInfo } = useUserInfo();
    const filteredItems =
        items?.filter((item) => {
            return isEmpty(item.requires) || item.requires.every((req) => userInfo[req]);
        }) ?? [];
    return (
        <ListDetailViewSectionLayout>
            <ListDetailViewSectionHeader title="Nastavení" role="list" compact={compact} />
            <ListDetailViewSectionContent>
                {filteredItems.map((item) => (
                    <ListViewItem
                        key={item.id}
                        mainText={item.name}
                        compact={compact}
                        detailId={detailId}
                        selected={detailId === item.id}
                        onClick={() => selectItem?.(item)}
                    />
                ))}
            </ListDetailViewSectionContent>
        </ListDetailViewSectionLayout>
    );
};

export const SettingsPage = () => {
    const pageState = usePageState();
    const { id: detailId } = useParams();
    const navigate = useNavigate();

    const { userInfo } = useUserInfo();
    const filteredItems =
        items?.filter((item) => {
            return isEmpty(item.requires) || item.requires.every((req) => userInfo[req]);
        }) ?? [];
    const itemsMap = filteredItems.reduce((acc, item) => {
        acc[item.id] = item;
        return acc;
    }, {});

    const data = itemsMap[detailId];
    const compact = false;

    if (isEmpty(data) || isEmpty(detailId)) {
        return <Navigate to={`/settings/${filteredItems[0]?.id}`} />;
    }

    return (
        <PageContext.Provider value={pageState}>
            <MainPage>
                <ListView>
                    <SettingsList
                        detailId={detailId}
                        selectItem={({ id }) => navigate(`/settings/${id}`)}
                    />
                </ListView>
                <MainView>
                    <ListDetailViewSectionLayout>
                        <ListDetailViewSectionHeader title={data?.name} compact={compact} />
                        <ListDetailViewSectionContent>
                            <Container maxWidth="lg">
                                {data?.render?.() || <div>Not implemented</div>}
                            </Container>
                        </ListDetailViewSectionContent>
                    </ListDetailViewSectionLayout>
                </MainView>
            </MainPage>
        </PageContext.Provider>
    );
};
